import { useCallback, useRef, useState } from 'react';
import { searchUsers, getSendbirdChannel } from '../../../api/supportPage';
import { getSendbird } from '../sendbird';
import type { GroupChannel } from '@sendbird/chat/groupChannel';
import { SendbirdUser } from '../chat';

const MIN_QUERY_LENGTH = 4;
const DEBOUNCE_MS = 300;

export const useSupportPage = (supportUser: SendbirdUser | null) => {
    const [query, setQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [users, setUsers] = useState<SendbirdUser[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [channelMap, setChannelMap] = useState<Record<string, GroupChannel>>({});

    const wrapperRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const fetchUsers = useCallback(async (value: string) => {
        if(value.trim().length < MIN_QUERY_LENGTH) {
            setUsers([]);
            setChannelMap({});
            return;
        }

        setIsLoading(true);
        setError(null);
        setChannelMap({});

        try {
            const res = await searchUsers({ query: value.trim() });
            setUsers(res.users);
        } catch {
            setError('Failed to search users. Please try again.');
            setUsers([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setQuery(value);
        setIsOpen(true);

        if(debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }

        debounceTimer.current = setTimeout(() => {
            fetchUsers(value);
        }, DEBOUNCE_MS);
    }, [fetchUsers]);

    const handleFocus = useCallback(() => setIsOpen(true), []);

    const handleBlur = useCallback((e: React.FocusEvent<HTMLDivElement>) => {
        if(!wrapperRef.current?.contains(e.relatedTarget as Node)) {
            setIsOpen(false);
        }
    }, []);

    const clearQuery = useCallback(() => {
        setQuery('');
        setUsers([]);
        setChannelMap({});
        setIsOpen(false);
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }
        inputRef.current?.focus();
    }, []);

    const handleSelect = useCallback((user: SendbirdUser) => {
        if (user?.email) {
            setQuery(user.email);
        }
        setIsOpen(false);
    }, []);

    const assignRoleToSupport = useCallback(async (): Promise<void> => {
        if (!supportUser) {
            throw new Error('No support user available.');
        }

        const sb = await getSendbird();

        try {
            await sb.connect(supportUser.user_id, supportUser.access_token);
        } catch (err: any) {
            const msg = err?.message ?? 'Unknown error';
            throw new Error(`Failed to connect to Sendbird: ${msg}`);
        }

        try {
            const meta = sb.currentUser?.metaData as Record<string, string> | undefined;
            if (!meta?.support_user_id) {
                await sb.currentUser!.updateMetaData({ support_user_id: supportUser.user_id }, true);
            }
        } catch (err: any) {
            const msg = err?.message ?? 'Unknown error';
            throw new Error(`Failed to assign support role: ${msg}`);
        } finally {
            await sb.disconnect();
        }
    }, [supportUser]);

    const claimUserToSupport = useCallback(async (
        support: SendbirdUser,
        user: SendbirdUser
    ): Promise<GroupChannel> => {
        if (!user.id) {
            throw new Error('User has no ID.');
        }

        if (!support.id) {
            throw new Error('Support user has no ID.');
        }

        const supportJoin = await getSendbirdChannel(user.id, support.id);
        if (!supportJoin?.channel_url || !supportJoin.support_access_token || !supportJoin.support_user_sendbird_id) {
            throw new Error('No Sendbird channel found for this user.');
        }

        const sb = await getSendbird();

        try {
            await sb.connect(supportJoin.support_user_sendbird_id, supportJoin.support_access_token);
        } catch (err: any) {
            const msg = err?.message ?? 'Unknown error';
            throw new Error(`Failed to connect support user to Sendbird: ${msg}`);
        }

        let channel: GroupChannel;
        try {
            channel = await sb.groupChannel.getChannel(supportJoin.channel_url);
        } catch (err: any) {
            await sb.disconnect();
            throw new Error(`Failed to get Sendbird channel: ${err?.message ?? 'Unknown error'}`);
        }

        return channel;
    }, [assignRoleToSupport]);

    return {
        query,
        isOpen,
        users,
        isLoading,
        error,
        channelMap,
        wrapperRef,
        inputRef,
        handleInputChange,
        handleFocus,
        handleBlur,
        handleSelect,
        clearQuery,
        claimUserToSupport,
        assignRoleToSupport,
    };
};
